// ═════════════════════════════════════════════════════════════════════════════
//  AFILIADOS · la mesa de control del negocio de profesores
//
//  Rob: «en el crm ponemos el porcentaje [...] administrar quien pasa como
//  teacher, asignarle tokens por si quieren probar la IA, borrar, editar,
//  aceptar». Y sobre todo: «tenemos que ser cuidadosos, porque se maneja
//  dinero».
//
//  ── POR QUE ESTA PESTAÑA NO ES UNA TABLA ────────────────────────────────────
//  Porque de todo lo que hay aquí, sólo UNA cosa pide una decisión hoy: un
//  profesor esperando que le paguen. Lo demás es consulta.
//
//  Una tabla trata las dos cosas igual y esconde la única urgente entre veinte
//  filas iguales. Por eso los cobros abiertos van ARRIBA, grandes y con sus dos
//  botones; y la lista de profesores va debajo, que es donde se mira cuando se
//  busca algo, no cuando hay que actuar.
//
//  ── LA REGLA DE LAS MONEDAS ─────────────────────────────────────────────────
//  Nunca se suman. Ni aquí ni en el servidor. Un total que junta pesos y
//  dólares no significa nada y ADEMÁS PARECE CORRECTO, que es lo peor de los
//  dos mundos: nadie lo cuestiona hasta que alguien paga de más.
//
//  ── POR QUE NO VIVE DENTRO DE `TeachersTab` ─────────────────────────────────
//  Son dos economías distintas sobre las mismas personas. En `TeachersTab` el
//  profesor cobra por CLASES DADAS —nómina, tarifa por clase—. Aquí cobra un
//  PORCENTAJE de lo que pagan los alumnos que trajo. Los saldos no son
//  intercambiables, y el día que las dos cifras aparezcan en la misma tarjeta,
//  alguien va a pagar una creyendo que paga la otra.
// ═════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, BadgeCheck, Check, Copy, Link2, RefreshCw, Search,
  UserPlus, Wallet, X, Zap,
} from 'lucide-react';

import api from '../../../../api/apiClient';
import './AfiliadosTab.css';

/** Dinero SIEMPRE con su moneda al lado. Una cifra suelta es el principio de
 *  una discusión: 640 no es nada, 640 MXN sí. */
const money = (n, m) => `${(n || 0).toLocaleString('es-MX', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} ${m}`;

const miles = n => (n || 0).toLocaleString('es-MX');

const cuando = iso => (iso
  ? new Date(iso).toLocaleDateString('es-MX',
      { day: '2-digit', month: 'short', year: '2-digit' })
  : '—');

export default function AfiliadosTab({ t, HICOLOR, dark = true }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState(null);
  const [buscando, setBuscando] = useState('');
  const [abierto, setAbierto] = useState(null);      // fila desplegada
  const [rechazando, setRechazando] = useState(null); // cobro a rechazar
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  // El componente puede montarse suelto en un banco de pruebas, sin el panel
  // que le pasa los tokens. Se cae de pie con los del tema oscuro.
  const tk = t || {
    bg: dark ? '#1a1a1a' : '#e0e5ec',
    tx: dark ? '#e8e8f0' : '#1a1a2e',
    tx2: dark ? '#7a7a9a' : '#7a8499',
    ac: dark ? '#00FFFF' : '#ffff00',
    ok: dark ? '#00ff88' : '#00cc66',
    er: dark ? '#ff6b6b' : '#f87171',
    nmd: dark ? '-6px 6px 12px #0a0a0a, 6px -6px 12px #2a2a2a'
              : '-6px 6px 12px #a3b1c6, 6px -6px 12px #fff',
    nsm: dark ? '-3px 3px 8px #0a0a0a, 3px -3px 8px #2a2a2a'
              : '-3px 3px 8px #a3b1c6, 3px -3px 8px #fff',
    nxs: dark ? '-2px 2px 5px #0a0a0a, 2px -2px 5px #2a2a2a'
              : '-2px 2px 5px #a3b1c6, 2px -2px 5px #fff',
    ism: dark ? 'inset -3px 3px 8px #0a0a0a, inset 3px -3px 8px #2a2a2a'
              : 'inset -3px 3px 8px #a3b1c6, inset 3px -3px 8px #fff',
    ixs: dark ? 'inset -2px 2px 5px #0a0a0a, inset 2px -2px 5px #2a2a2a'
              : 'inset -2px 2px 5px #a3b1c6, inset 2px -2px 5px #fff',
  };
  const acento = HICOLOR || tk.ac;

  const vars = {
    '--af-bg': tk.bg, '--af-tx': tk.tx, '--af-tx2': tk.tx2,
    '--af-ac': acento, '--af-ok': tk.ok, '--af-er': tk.er,
    '--af-nmd': tk.nmd, '--af-nsm': tk.nsm, '--af-nxs': tk.nxs,
    '--af-ism': tk.ism, '--af-ixs': tk.ixs,
    '--af-linea': dark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.08)',
  };

  const carga = () => {
    setCargando(true);
    api.get('/api/admin/afiliados')
      .then(r => { setDatos(r.data); setError(''); })
      .catch(() => setError('No se pudo cargar. ¿Está levantado el backend?'))
      .finally(() => setCargando(false));
  };

  useEffect(carga, []);

  /* ── Acciones ─────────────────────────────────────────────────────────── */

  const cuenta = (accion, cuerpo) => {
    setOcupado(true);
    setAviso(null);
    return accion(cuerpo)
      .then(r => { setAviso({ ok: true, texto: r }); carga(); })
      .catch(e => setAviso({
        ok: false,
        texto: e?.response?.data?.detail || 'No se pudo completar.',
      }))
      .finally(() => setOcupado(false));
  };

  const pagar = (c) => {
    // Se confirma porque esto NO transfiere dinero: sólo deja constancia de que
    // Rob ya lo hizo por su banco. Marcarlo sin haber transferido hace que el
    // profesor vea «pagado» algo que no le llegó, y eso se descubre siempre.
    const seguro = window.confirm(
      `¿Ya le transferiste ${money(c.importe, c.moneda)} a ${c.nombre}?\n\n`
      + 'Esto NO envía dinero: sólo lo marca como pagado en NSL.');
    if (!seguro) return;
    cuenta(() => api.post(`/api/admin/afiliados/cobros/${c.id}/pagar`)
      .then(r => `Liquidadas ${r.data.comisiones_liquidadas} comisiones `
                 + `(${money(r.data.importe, r.data.moneda)}).`));
  };

  const rechazar = () => {
    const m = motivo.trim();
    if (m.length < 5) return;
    cuenta(() => api.post(
      `/api/admin/afiliados/cobros/${rechazando.id}/rechazar`, { motivo: m })
      .then(() => 'Cobro rechazado. El saldo del profesor sigue intacto.'))
      .then(() => { setRechazando(null); setMotivo(''); });
  };

  const barrer = () => cuenta(() => api.post('/api/admin/afiliados/barrer')
    .then(r => (r.data.creadas
      ? `Se recuperaron ${r.data.creadas} comisiones que faltaban.`
      : 'Nada que recuperar: todos los pagos tienen su comisión.')));

  const totales = datos?.totales || {};
  const monedas = Object.keys(totales);
  const res = datos?.resumen || {};

  const lista = useMemo(() => {
    const q = buscando.trim().toLowerCase();
    const base = datos?.profesores || [];
    if (!q) return base;
    return base.filter(p => (p.nombre || '').toLowerCase().includes(q)
                         || (p.email || '').toLowerCase().includes(q)
                         || (p.afiliado?.codigo || '').toLowerCase().includes(q));
  }, [datos, buscando]);

  if (cargando && !datos) {
    return <div className="afil" style={vars}><div className="afil-cargando">Cargando…</div></div>;
  }

  return (
    <div className="afil" style={vars}>
      {error && (
        <div className="afil-error"><AlertTriangle size={14} /> {error}</div>
      )}

      {aviso && (
        <div className={`afil-aviso ${aviso.ok ? 'afil-aviso-ok' : 'afil-aviso-er'}`}>
          {aviso.ok ? <Check size={14} /> : <AlertTriangle size={14} />}
          <span>{typeof aviso.texto === 'string' ? aviso.texto : ''}</span>
          <button type="button" className="afil-x" onClick={() => setAviso(null)}>
            <X size={13} />
          </button>
        </div>
      )}

      {/* ══ LO QUE PIDE UNA DECISIÓN HOY ════════════════════════════════════
          Va primero y va grande. Es lo único de esta pantalla donde hay una
          persona esperando: todo lo demás es consulta.                     */}
      <section className="afil-caja afil-cobros">
        <div className="afil-rotulo">
          <Wallet size={12} /> Cobros por resolver
        </div>

        {(datos?.cobros_abiertos || []).length === 0 ? (
          <p className="afil-vacio">
            Nadie está esperando un pago. Cuando un profesor pida cobrar,
            aparecerá aquí.
          </p>
        ) : (
          <div className="afil-cobro-lista">
            {datos.cobros_abiertos.map(c => (
              <div className="afil-cobro" key={c.id}>
                <div className="afil-cobro-quien">
                  <b>{c.nombre}</b>
                  <span className="afil-tenue">
                    solicitud #{c.id} · {cuando(c.cuando)}
                  </span>
                </div>
                <div className="afil-cobro-cifra afil-mono">
                  {money(c.importe, c.moneda)}
                </div>
                <div className="afil-cobro-acciones">
                  <button type="button" className="afil-btn afil-btn-ok"
                          disabled={ocupado} onClick={() => pagar(c)}>
                    <Check size={13} /> Pagado
                  </button>
                  <button type="button" className="afil-btn afil-btn-no"
                          disabled={ocupado}
                          onClick={() => { setRechazando(c); setMotivo(''); }}>
                    <X size={13} /> Rechazar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* El motivo del rechazo. Obligatorio y CON CONTADOR a la vista: al
            otro lado hay alguien esperando su dinero, y un rechazo sin motivo
            es un mensaje a soporte garantizado. */}
        {rechazando && (
          <div className="afil-rechazo">
            <div className="afil-rotulo">
              Por qué se rechaza el cobro de {rechazando.nombre}
            </div>
            <textarea
              className="afil-texto"
              rows={2}
              maxLength={300}
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Ej.: ya se le pagó por transferencia el 8/9, fuera del sistema"
            />
            <div className="afil-contador">
              <span className={motivo.trim().length < 5 ? 'afil-falta' : ''}>
                {motivo.trim().length < 5
                  ? `Faltan ${5 - motivo.trim().length} caracteres`
                  : 'Listo'}
              </span>
              <span className="afil-tenue">{motivo.length}/300</span>
            </div>
            <div className="afil-cobro-acciones">
              <button type="button" className="afil-btn afil-btn-no"
                      disabled={ocupado || motivo.trim().length < 5}
                      onClick={rechazar}>
                Rechazar el cobro
              </button>
              <button type="button" className="afil-btn"
                      onClick={() => { setRechazando(null); setMotivo(''); }}>
                Cancelar
              </button>
            </div>
            <p className="afil-nota">
              Rechazar el cobro NO borra lo ganado: sus comisiones siguen
              pendientes y puede volver a pedirlo.
            </p>
          </div>
        )}
      </section>

      {/* ══ EL TITULAR ══════════════════════════════════════════════════════ */}
      <section className="afil-titular">
        <div className="afil-caja afil-kpi">
          <span className="afil-rotulo">Profesores</span>
          <b className="afil-cifra afil-mono">{miles(res.profesores)}</b>
          <span className="afil-tenue">
            {miles(res.afiliados_activos)} con código activo
          </span>
        </div>
        <div className="afil-caja afil-kpi">
          <span className="afil-rotulo">Alumnos traídos</span>
          <b className="afil-cifra afil-mono">{miles(res.referidos)}</b>
          <span className="afil-tenue">por sus enlaces</span>
        </div>

        {/* Por moneda, NUNCA sumadas. */}
        {monedas.map(m => (
          <div className="afil-caja afil-kpi afil-kpi-dinero" key={m}>
            <span className="afil-rotulo">Por pagar · {m}</span>
            <b className="afil-cifra afil-mono">{money(totales[m].pendiente, m)}</b>
            <span className="afil-tenue">
              ya pagado {money(totales[m].pagada, m)}
            </span>
          </div>
        ))}

        {monedas.length === 0 && (
          <div className="afil-caja afil-kpi">
            <span className="afil-rotulo">Comisiones</span>
            <b className="afil-cifra afil-mono">—</b>
            <span className="afil-tenue">todavía ninguna</span>
          </div>
        )}
      </section>

      {/* ══ LA LISTA ════════════════════════════════════════════════════════ */}
      <section className="afil-caja afil-lista-caja">
        <div className="afil-barra">
          <div className="afil-buscar">
            <Search size={13} />
            <input
              value={buscando}
              onChange={e => setBuscando(e.target.value)}
              placeholder="Buscar por nombre, correo o código"
              maxLength={60}
            />
          </div>
          <button type="button" className="afil-btn" onClick={carga} disabled={ocupado}>
            <RefreshCw size={13} /> Actualizar
          </button>
          {/* La red de seguridad. Ver `comisiones.barre()`: hoy seis caminos
              avisan al libro, y el séptimo se va a olvidar. */}
          <button type="button" className="afil-btn" onClick={barrer} disabled={ocupado}
                  title="Busca pagos cobrados que se quedaron sin comisión. Es seguro pulsarlo las veces que haga falta.">
            <BadgeCheck size={13} /> Revisar comisiones
          </button>
        </div>

        {lista.length === 0 && (
          <p className="afil-vacio">Ningún profesor coincide con esa búsqueda.</p>
        )}

        <div className="afil-filas">
          {lista.map(p => (
            <FilaProfesor
              key={p.id}
              p={p}
              abierta={abierto === p.id}
              onAbrir={() => setAbierto(abierto === p.id ? null : p.id)}
              onHecho={(texto) => { setAviso({ ok: true, texto }); carga(); }}
              onFallo={(texto) => setAviso({ ok: false, texto })}
            />
          ))}
        </div>
      </section>

      <ConvertirProfesor onHecho={(texto) => { setAviso({ ok: true, texto }); carga(); }} />
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
   UNA FILA DE PROFESOR

   Cerrada enseña lo que se consulta; abierta, lo que se hace. Todo a la vista a
   la vez convierte veinte profesores en una pared de botones donde el de
   «regalar Energía» está a un centímetro del de «cambiar porcentaje».
   ═════════════════════════════════════════════════════════════════════════ */

function FilaProfesor({ p, abierta, onAbrir, onHecho, onFallo }) {
  const af = p.afiliado;
  const [pct, setPct] = useState(af?.porcentaje ?? 20);
  const [estado, setEstado] = useState(af?.estado ?? 'activo');
  const [energia, setEnergia] = useState(1000);
  const [ocupado, setOcupado] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const monedas = Object.keys(p.por_moneda || {});

  const enlace = af ? `${window.location.origin}/?ref=${af.codigo}` : '';

  const copia = () => {
    try {
      navigator.clipboard.writeText(enlace);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch { /* sin permiso de portapapeles */ }
  };

  const guarda = () => {
    setOcupado(true);
    const cuerpo = { porcentaje: Number(pct), estado, notas: af?.notas || null };
    const peticion = af
      ? api.put(`/api/admin/teachers/${p.id}/afiliado`, cuerpo)
      : api.post(`/api/admin/teachers/${p.id}/afiliado`, cuerpo);
    peticion
      .then(r => onHecho(af
        ? `${p.nombre}: reparto al ${r.data.afiliado.porcentaje}% (${estado}).`
        : `${p.nombre} ya es afiliado. Su código es ${r.data.afiliado.codigo}.`))
      .catch(e => onFallo(e?.response?.data?.detail || 'No se pudo guardar.'))
      .finally(() => setOcupado(false));
  };

  const dar = () => {
    setOcupado(true);
    api.post(`/api/admin/afiliados/${p.id}/energia`, { tokens: Number(energia) })
      .then(r => onHecho(
        `${p.nombre}: +${miles(r.data.acreditado)} de Energía Spacy`
        + (r.data.plan_puesto
          ? `. No tenía plan, así que se le puso «${r.data.plan_puesto}» — sin plan, el saldo no le habría servido de nada.`
          : `. Le quedan ${miles(r.data.restantes)}.`)))
      .catch(e => onFallo(e?.response?.data?.detail || 'No se pudo acreditar.'))
      .finally(() => setOcupado(false));
  };

  return (
    <div className={`afil-fila ${abierta ? 'afil-fila-on' : ''}`}>
      <button type="button" className="afil-fila-cab" onClick={onAbrir}>
        <div className="afil-fila-quien">
          <b>{p.nombre}</b>
          <span className="afil-tenue">{p.email}</span>
        </div>

        <div className="afil-fila-codigo">
          {af ? (
            <>
              <span className="afil-chip afil-mono">{af.codigo}</span>
              <span className={`afil-chip ${af.estado === 'activo' ? 'afil-chip-ok' : 'afil-chip-pausa'}`}>
                {af.estado === 'activo' ? `${af.porcentaje}%` : 'en pausa'}
              </span>
            </>
          ) : (
            <span className="afil-chip afil-chip-no">sin código</span>
          )}
        </div>

        <div className="afil-fila-cifras">
          <span title="Alumnos que trajo">
            <b className="afil-mono">{miles(p.referidos)}</b> traídos
          </span>
          {monedas.map(m => (
            <span key={m} title={`Pendiente de pagarle en ${m}`}>
              <b className="afil-mono">{money(p.por_moneda[m].pendiente, m)}</b>
            </span>
          ))}
          <span title="Energía Spacy que le queda">
            <Zap size={11} /> <b className="afil-mono">{miles(p.energia.restantes)}</b>
          </span>
        </div>
      </button>

      {abierta && (
        <div className="afil-fila-cuerpo">
          {af && (
            <div className="afil-enlace">
              <Link2 size={12} />
              <code>{enlace}</code>
              <button type="button" className="afil-btn afil-btn-min" onClick={copia}>
                <Copy size={12} /> {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          )}

          <div className="afil-acciones">
            {/* ── EL REPARTO ─────────────────────────────────────────────── */}
            <div className="afil-accion">
              <span className="afil-rotulo">
                {af ? 'Cambiar su reparto' : 'Darle de alta como afiliado'}
              </span>
              <div className="afil-campos">
                <label className="afil-campo">
                  <span>Porcentaje</span>
                  <input type="number" min={0} max={100} step={0.5}
                         value={pct} onChange={e => setPct(e.target.value)} />
                </label>
                <label className="afil-campo">
                  <span>Estado</span>
                  <select value={estado} onChange={e => setEstado(e.target.value)}>
                    <option value="activo">Activo</option>
                    <option value="pausado">En pausa</option>
                  </select>
                </label>
                <button type="button" className="afil-btn afil-btn-ac"
                        disabled={ocupado} onClick={guarda}>
                  {af ? 'Guardar' : <><UserPlus size={13} /> Dar de alta</>}
                </button>
              </div>
              <p className="afil-nota">
                {af
                  ? 'Cambiar el porcentaje NO repaga hacia atrás: cada comisión guarda el suyo. Pausar deja de atribuir alumnos nuevos, pero lo ganado sigue siendo suyo.'
                  : 'Se le genera un código único. No se puede elegir: es su identidad pública y uno a medida sería suplantable.'}
              </p>
            </div>

            {/* ── LA ENERGÍA ─────────────────────────────────────────────── */}
            <div className="afil-accion">
              <span className="afil-rotulo">Regalarle Energía Spacy</span>
              <div className="afil-campos">
                <label className="afil-campo">
                  <span>Tokens</span>
                  <input type="number" min={1} max={20000} step={100}
                         value={energia} onChange={e => setEnergia(e.target.value)} />
                </label>
                <button type="button" className="afil-btn"
                        disabled={ocupado} onClick={dar}>
                  <Zap size={13} /> Acreditar
                </button>
              </div>
              <p className="afil-nota">
                {p.energia.tope > 0
                  ? `Plan «${p.energia.plan}»${p.energia.sin_plan ? ' (sin asignar: se le mide con ese tope)' : ''}`
                    + ` · le quedan ${miles(p.energia.restantes)} de ${miles(p.energia.tope)}`
                    + (p.energia.renueva
                      ? ` · se renuevan el ${new Date(`${p.energia.renueva}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}.`
                      : '.')
                  : 'No se pudo leer su energía de IA.'}
              </p>
            </div>
          </div>

          {monedas.length > 0 && (
            <div className="afil-detalle-monedas">
              {monedas.map(m => (
                <span key={m}>
                  <b className="afil-mono">{money(p.por_moneda[m].pendiente, m)}</b> por pagar
                  {' · '}
                  <b className="afil-mono">{money(p.por_moneda[m].pagada, m)}</b> pagado
                  {p.por_moneda[m].anulada > 0 && (
                    <> {' · '}<b className="afil-mono">{money(p.por_moneda[m].anulada, m)}</b> anulado</>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
   CONVERTIR A ALGUIEN EN PROFESOR

   Rob pidió «administrar quién pasa como teacher». No hay cola de solicitudes
   porque todavía no existe un formulario público donde apuntarse: hoy esa
   conversación pasa por fuera y quien decide es él. Esto es lo que hace falta
   para ejecutar la decisión.

   La búsqueda pide dos letras y devuelve doce como mucho. Volcar la base de
   alumnos en un desplegable no es una función, es una fuga.
   ═════════════════════════════════════════════════════════════════════════ */

function ConvertirProfesor({ onHecho }) {
  const [q, setQ] = useState('');
  const [gente, setGente] = useState([]);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    const termino = q.trim();
    if (termino.length < 2) { setGente([]); return undefined; }
    const id = setTimeout(() => {
      api.get(`/api/admin/afiliados/candidatos?q=${encodeURIComponent(termino)}`)
        .then(r => setGente(r.data?.candidatos || []))
        .catch(() => setGente([]));
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const convierte = (u) => {
    if (!window.confirm(
      `¿Convertir a ${u.nombre} en profesor?\n\n`
      + 'Va a poder entrar al portal del profesor y ver a los alumnos que se '
      + 'le asignen.')) return;
    setOcupado(true);
    // El cuerpo vacío NO sobra: la ruta pide un ConvertirPayload y todos sus
    // campos tienen valor por defecto, pero FastAPI sigue exigiendo que el
    // cuerpo VENGA. Sin él son 422 y el botón no hace nada — medido llamando
    // a la ruta con un usuario inexistente: sin cuerpo da 422, con `{}` da 404.
    api.post(`/api/admin/teachers/convertir/${u.id}`, {})
      .then(() => { onHecho(`${u.nombre} ya es profesor.`); setQ(''); })
      .catch(e => window.alert(e?.response?.data?.detail || 'No se pudo convertir.'))
      .finally(() => setOcupado(false));
  };

  return (
    <section className="afil-caja">
      <div className="afil-rotulo"><UserPlus size={12} /> Convertir a alguien en profesor</div>
      <div className="afil-buscar afil-buscar-solo">
        <Search size={13} />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Nombre o correo (mínimo 2 letras)"
          maxLength={60}
        />
      </div>

      {q.trim().length >= 2 && gente.length === 0 && (
        <p className="afil-vacio">Nadie con ese nombre que no sea ya profesor.</p>
      )}

      <div className="afil-candidatos">
        {gente.map(u => (
          <div className="afil-candidato" key={u.id}>
            <div>
              <b>{u.nombre}</b>
              <span className="afil-tenue">{u.email} · {u.tipo}</span>
            </div>
            <button type="button" className="afil-btn afil-btn-ac"
                    disabled={ocupado} onClick={() => convierte(u)}>
              Convertir
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
